import { AssociationType } from "@/shared/domain/association";
import type { AssociationRecord } from "@/shared/domain/association";
import type { CreateEventRequest } from "@/shared/domain/events/EventRecord";

import { ApprovalMode } from "./approvalModes";
import type { ClientToolDeps, ClientToolExecutionResult } from "./ClientTools";
import { createClientTool } from "./ClientTools";
import { CreateEventsConfirmation } from "./CreateEventsConfirmation";

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

interface EventAssociationInput {
  dataset_id?: string;
  file_id?: string;
  topic_id?: string;
  message_path_id?: string;
}

interface EventInput {
  name: string;
  description?: string;
  start_time: number;
  end_time: number;
  associations: EventAssociationInput[];
  tags?: string[];
  metadata?: Record<string, unknown>;
  display_options?: {
    color?: string;
  };
}

export interface CreateEventsInput {
  events: EventInput[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Convert the LLM's association input into the `AssociationRecord` shape
 * expected by the backend.
 */
function toAssociationRecord(input: EventAssociationInput): AssociationRecord {
  if (input.dataset_id) {
    return {
      association_id: input.dataset_id,
      association_type: AssociationType.Dataset,
    };
  }
  if (input.file_id) {
    return {
      association_id: input.file_id,
      association_type: AssociationType.File,
    };
  }
  if (input.topic_id) {
    return {
      association_id: input.topic_id,
      association_type: AssociationType.Topic,
    };
  }
  if (input.message_path_id) {
    return {
      association_id: input.message_path_id,
      association_type: AssociationType.MessagePath,
    };
  }
  throw new Error("Association must have at least one ID field set");
}

function toCreateEventRequest(event: EventInput): CreateEventRequest {
  return {
    name: event.name,
    description: event.description,
    start_time: BigInt(event.start_time),
    end_time: BigInt(event.end_time),
    associations: event.associations.map(toAssociationRecord),
    tags: event.tags ?? [],
    metadata: event.metadata ?? {},
    display_options: event.display_options,
  };
}

// ---------------------------------------------------------------------------
// Tool spec
// ---------------------------------------------------------------------------

const inputSchema: Record<string, unknown> = {
  type: "object",
  properties: {
    events: {
      type: "array",
      description: "Array of events to create.",
      items: {
        type: "object",
        properties: {
          name: {
            type: "string",
            description:
              "Brief name for the event (e.g., 'CPU Spike', 'Takeoff', 'Crash').",
          },
          description: {
            type: "string",
            description: "Optional human-readable description of the event.",
          },
          start_time: {
            type: "integer",
            description: "Start time in nanoseconds since Unix epoch.",
          },
          end_time: {
            type: "integer",
            description:
              "End time in nanoseconds since Unix epoch. Equal to start_time for instantaneous events.",
          },
          associations: {
            type: "array",
            description:
              "Data entities this event is associated with. At least one required.",
            items: {
              type: "object",
              properties: {
                dataset_id: { type: "string" },
                file_id: { type: "string" },
                topic_id: { type: "string" },
                message_path_id: { type: "string" },
              },
            },
          },
          tags: {
            type: "array",
            items: { type: "string" },
            description: "Optional tags for categorization.",
          },
          metadata: {
            type: "object",
            description: "Optional key-value metadata for discovery.",
          },
          display_options: {
            type: "object",
            properties: {
              color: {
                type: "string",
                description:
                  "CSS color for visualization (hex, rgb, or named color).",
              },
            },
          },
        },
        required: ["name", "start_time", "end_time", "associations"],
      },
    },
  },
  required: ["events"],
};

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

async function execute(
  input: CreateEventsInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  if (!deps?.eventService || !deps?.orgId) {
    return {
      response: { error: "Event service or org ID not available" },
      success: false,
    };
  }

  if (!Array.isArray(input.events) || input.events.length === 0) {
    return {
      response: { error: "events array is required and must not be empty" },
      success: false,
    };
  }

  const results: Array<{
    name: string;
    event_id?: string;
    error?: string;
  }> = [];

  for (const event of input.events) {
    try {
      const request = toCreateEventRequest(event);
      const created = await deps.eventService.createEvent(deps.orgId, request);
      results.push({ name: event.name, event_id: created.event_id });
    } catch (err) {
      results.push({
        name: event.name,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  const anySucceeded = results.some((r) => r.event_id !== undefined);
  const allSucceeded = results.every((r) => r.event_id !== undefined);

  if (anySucceeded) {
    deps.onEventCreated?.();
  }

  return {
    response: {
      created_events: results,
      total: results.length,
      succeeded: results.filter((r) => r.event_id !== undefined).length,
    },
    success: allSucceeded,
  };
}

// ---------------------------------------------------------------------------
// Tool module export
// ---------------------------------------------------------------------------

export const tool = createClientTool<CreateEventsInput>({
  name: "client_create_events",
  description:
    "Create one or more time-based event annotations on datasets. " +
    "Events mark interesting time ranges (e.g., sensor anomalies, system events, " +
    "behavioral patterns) and can be associated with datasets, files, topics, " +
    "or message paths. Timestamps are in nanoseconds since Unix epoch.",
  input_schema: inputSchema,
  execute,
  defaultApprovalMode: ApprovalMode.REQUIRE_APPROVAL,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Create Events",
  displayDescription: "Creates time-based event annotations on datasets.",
  ConfirmationContent: CreateEventsConfirmation,
});
